import { useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { externalPreload } from '@/utils/dom'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import PageTitle from '@/components/PageTitle'
import Footer from '@/components/Footer'
import ListElement from '@/components/ListElement'
import Meta from '@/components/Meta'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const CATEGORIES_ID_TO_EXCLUDE = [98, 99] // ID Categoria Bozza

const ArchiveNews = ({
  cpt: cptFromProps,
  stub,
}) => {
  const { headerHeight } = useContext(GeneralContext)
  const { setReady } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight })
  const { getImageUrl } = useContext(GeneralContext)
  const [load, setLoad] = useState(false)
  const [archive, setArchive] = useState([])
  const $root = useRef()

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, pages, listCpt, categories } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub],
    pages: state.page.pages,
    listCpt: state.page.archive[cptFromProps],
    categories: state.page.taxonomies.categories,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const getCPT = useCallback((cpt, index, order) => dispatch(actions.page.getListCPT(cpt, index, order)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getTaxonomiesList = useCallback((data) => dispatch(actions.page.getAllTerms(data)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
    getCPT(cptFromProps, 100, 'desc')
    getTaxonomiesList('categories')
  }

  /*------------------------------
  If there are products & page is loaded from redux, complete the preload
  ------------------------------*/
  useEffect(() => {
    if (archive.length > 0 && page !== undefined) completePreload()
  }, [archive, page])

  /*------------------------------
  Map Categories to Product
  ------------------------------*/
  useEffect(() => {
    if (listCpt && categories) {
      const listMapped = listCpt
        .map((cat) => ({
          id: pages[cat].id,
          title: pages[cat].title.rendered,
          slug: pages[cat].slug,
          link: pages[cat].link,
          image: getImageUrl(pages[cat].featured_media, 'large', cat, 'pages'),
          categoriesId: pages[cat].categories,
          categories: pages[cat].categories.map((ct) => categories.find((c) => Number(c.id) === Number(ct)).name).join(' / '),
        }))
        .filter((item) => !item.categoriesId.some((it) => CATEGORIES_ID_TO_EXCLUDE.includes(it)))
      setArchive(listMapped)
    }
  }, [listCpt, categories])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render List
  ------------------------------*/
  const renderList = () => {
    return (
      <>
        <div className={classes.listIntro}>
          <PageTitle
            title={page.title.rendered}
            parallax
            delay={1}
          />
        </div>
        <div className={classes.list}>
          {archive.map((news) => (
            <ListElement
              key={news.id}
              item={news}
            />
          ))}
        </div>
      </>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderList()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveNews ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default ArchiveNews
